<template>
  <div class="markdown-with-screenshots" v-html="processedMarkdown"></div>
</template>

<script setup lang="ts">
import { computed, onMounted, nextTick, watch, ref } from 'vue';
import MarkdownIt from 'markdown-it';
import Screenshot from '@/storybook/templates/smart_actions/Screenshot.vue';
import { createApp } from 'vue';

interface Props {
  source: string;
  linkify?: boolean;
  typographer?: boolean;
  breaks?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  linkify: false,
  typographer: false,
  breaks: false,
});

const md = new MarkdownIt({
  linkify: props.linkify,
  typographer: props.typographer,
  breaks: props.breaks,
  html: true,
});

// Custom renderer for links to handle linked images
const defaultLinkOpenRenderer =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

const defaultLinkCloseRenderer =
  md.renderer.rules.link_close ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

// Custom renderer for images to create placeholder divs
const defaultImageRenderer =
  md.renderer.rules.image ||
  function (tokens, idx, options, env, renderer) {
    return renderer.renderToken(tokens, idx, options);
  };

md.renderer.rules.link_open = function (tokens, idx, options, env, renderer) {
  const token = tokens[idx];
  const hrefIndex = token.attrIndex('href');

  // Check if the next token is an image (indicating [![alt](src)](href) pattern)
  if (hrefIndex >= 0 && idx + 2 < tokens.length && tokens[idx + 1].type === 'image') {
    const href = token.attrs![hrefIndex][1];
    const imageToken = tokens[idx + 1];
    const srcIndex = imageToken.attrIndex('src');
    const altIndex = imageToken.attrIndex('alt');

    if (srcIndex >= 0) {
      const src = imageToken.attrs![srcIndex][1];
      const alt = altIndex >= 0 ? imageToken.attrs![altIndex][1] : '';

      // Create a unique ID for this screenshot
      const screenshotId = `screenshot-${Math.random().toString(36).substr(2, 9)}`;

      // Store the expanded URL in the environment for the image renderer
      env.screenshotData = env.screenshotData || {};
      env.screenshotData[screenshotId] = { src, alt, expandedSrc: href };

      return `<div class="screenshot-placeholder" data-screenshot-id="${screenshotId}" data-src="${src}" data-alt="${alt}" data-expanded-src="${href}"></div>`;
    }
  }

  return defaultLinkOpenRenderer(tokens, idx, options, env, renderer);
};

md.renderer.rules.image = function (tokens, idx, options, env, renderer) {
  const token = tokens[idx];

  // Check if this image is part of a linked image (skip rendering if so)
  if (idx > 0 && tokens[idx - 1].type === 'link_open') {
    return ''; // Skip rendering, handled by link_open
  }

  const srcIndex = token.attrIndex('src');
  const altIndex = token.attrIndex('alt');

  if (srcIndex >= 0) {
    const src = token.attrs![srcIndex][1];
    const alt = altIndex >= 0 ? token.attrs![altIndex][1] : '';

    // Create a unique ID for this screenshot
    const screenshotId = `screenshot-${Math.random().toString(36).substr(2, 9)}`;

    return `<div class="screenshot-placeholder" data-screenshot-id="${screenshotId}" data-src="${src}" data-alt="${alt}"></div>`;
  }

  return defaultImageRenderer(tokens, idx, options, env, renderer);
};

md.renderer.rules.link_close = function (tokens, idx, options, env, renderer) {
  // Check if this closes a linked image
  if (idx > 1 && tokens[idx - 2].type === 'link_open' && tokens[idx - 1].type === 'image') {
    return ''; // Skip rendering, handled by link_open
  }

  return defaultLinkCloseRenderer(tokens, idx, options, env, renderer);
};

const processedMarkdown = computed(() => {
  return md.render(props.source);
});

const mountedApps = ref<any[]>([]);

const mountScreenshots = () => {
  nextTick(() => {
    // Clean up existing apps
    mountedApps.value.forEach(app => {
      try {
        app.unmount();
      } catch {
        // Ignore unmount errors
      }
    });
    mountedApps.value = [];

    // Find all screenshot placeholders and mount Screenshot components
    const placeholders = document.querySelectorAll('.screenshot-placeholder');

    placeholders.forEach(placeholder => {
      const src = placeholder.getAttribute('data-src');
      const alt = placeholder.getAttribute('data-alt') || '';
      const expandedSrc = placeholder.getAttribute('data-expanded-src');

      if (src && placeholder instanceof HTMLElement) {
        // Create Vue app for this Screenshot component
        const app = createApp(Screenshot, {
          src: src,
          expandedSrc: expandedSrc || src, // Use expandedSrc if available, otherwise fallback to src
          alt: alt,
        });

        // Clear the placeholder content and mount the component
        placeholder.innerHTML = '';
        app.mount(placeholder);
        mountedApps.value.push(app);
      }
    });
  });
};

onMounted(mountScreenshots);
watch(() => props.source, mountScreenshots);
</script>

<style scoped>
.markdown-with-screenshots {
  display: flex;
  flex-direction: column;
}

.markdown-with-screenshots :deep(h1),
.markdown-with-screenshots :deep(h2),
.markdown-with-screenshots :deep(h3),
.markdown-with-screenshots :deep(h4),
.markdown-with-screenshots :deep(h5),
.markdown-with-screenshots :deep(h6) {
  font-weight: bold;
  margin-top: 0.75rem;
  margin-bottom: 0.25rem;
}

.markdown-with-screenshots :deep(h1:first-child),
.markdown-with-screenshots :deep(h2:first-child),
.markdown-with-screenshots :deep(h3:first-child),
.markdown-with-screenshots :deep(h4:first-child),
.markdown-with-screenshots :deep(h5:first-child),
.markdown-with-screenshots :deep(h6:first-child) {
  margin-top: 0;
}

.markdown-with-screenshots :deep(h1) {
  font-size: 1.5rem;
}
.markdown-with-screenshots :deep(h2) {
  font-size: 1.25rem;
}
.markdown-with-screenshots :deep(h3) {
  font-size: 1.125rem;
}

.markdown-with-screenshots :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-with-screenshots :deep(ul),
.markdown-with-screenshots :deep(ol) {
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
}

.markdown-with-screenshots :deep(li) {
  margin-bottom: 0.25rem;
}

.markdown-with-screenshots :deep(blockquote) {
  border-left: 4px solid #d1d5db;
  padding-left: 1rem;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.markdown-with-screenshots :deep(code) {
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.markdown-with-screenshots :deep(pre) {
  background-color: #f3f4f6;
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 0.5rem;
  overflow-x: auto;
}

.dark .markdown-with-screenshots :deep(code),
.dark .markdown-with-screenshots :deep(pre) {
  background-color: #374151;
}

.markdown-with-screenshots :deep(.screenshot-placeholder) {
  margin: 0.5rem 0;
}
</style>
